import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import type { MessageTimeSeriesPeriod } from '@/types/analytics'

interface AvgMessagesChartProps {
  data: MessageTimeSeriesPeriod[]
  dateRangeLabel?: string
}

const chartConfig = {
  avgPerConversation: {
    label: 'Avg. messages',
    color: 'hsl(var(--chart-1))',
  },
} satisfies ChartConfig

const chartClassNames = {
  card: 'flex-1 rounded-2xl shadow-sm',
  header: 'pb-2',
  title: 'text-base font-semibold leading-4',
  subtitle: 'text-sm text-muted-foreground',
  content: 'pt-0',
}

export function AvgMessagesChart({
  data,
  dateRangeLabel,
}: AvgMessagesChartProps) {
  // Format period labels for display
  const formattedData = data.map((item) => ({
    ...item,
    label: formatPeriodLabel(item.period),
  }))

  return (
    <Card className={chartClassNames.card}>
      <CardHeader className={chartClassNames.header}>
        <CardTitle className={chartClassNames.title}>Avg. Messages</CardTitle>
        {dateRangeLabel && (
          <p className={chartClassNames.subtitle}>{dateRangeLabel}</p>
        )}
      </CardHeader>
      <CardContent className={chartClassNames.content}>
        <ChartContainer config={chartConfig} className="h-[250px] w-full">
          <BarChart data={formattedData} accessibilityLayer>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
            />
            <YAxis tickLine={false} axisLine={false} tickMargin={8} />
            <ChartTooltip content={<ChartTooltipContent />} />
            {/* recharts 3 injects the real `payload`/`verticalAlign` into the
                content element via cloneElement at render time; the `payload={[]}`
                default only satisfies the now-required prop type and is overwritten
                before render, so the rendered legend is unchanged. */}
            <ChartLegend content={<ChartLegendContent payload={[]} />} />
            <Bar
              dataKey="avgPerConversation"
              fill="var(--color-avgPerConversation)"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

function formatPeriodLabel(period: string): string {
  // Handle different period formats
  if (period.includes('-W')) {
    // Weekly format: "2025-W01" -> "Jan 6" (start of week)
    const match = period.match(/^(\d{4})-W(\d{2})$/)
    if (match) {
      const year = Number(match[1])
      const week = Number(match[2])
      // Calculate the Monday of the given ISO week
      const jan4 = new Date(year, 0, 4)
      const dayOfWeek = jan4.getDay() || 7
      const firstMonday = new Date(jan4)
      firstMonday.setDate(jan4.getDate() - dayOfWeek + 1)
      const weekStart = new Date(firstMonday)
      weekStart.setDate(firstMonday.getDate() + (week - 1) * 7)
      return weekStart.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    }
    return period
  }
  if (period.match(/^\d{4}-\d{2}$/)) {
    // Monthly format: "2025-01" -> "Jan"
    const [year, month] = period.split('-')
    const date = new Date(Number(year), Number(month) - 1)
    return date.toLocaleDateString('en-US', { month: 'short' })
  }
  if (period.match(/^\d{4}-\d{2}-\d{2}$/)) {
    // Daily format: "2025-01-15" -> "Jan 15"
    const date = new Date(period)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  return period
}
